import { useCallback, useEffect, useState } from 'react';
import { resolveBanepadDevice } from '../../lib/banepadDevices';
import { clearPadLastKnown } from '../../lib/banepadLastKnown';
import {
  clearBanepadDevicePairing,
  isBanepadDevicePaired,
  markBanepadDevicePaired,
  retireLegacyDeviceToken,
} from '../../lib/banepadDeviceSession';
import type { BanepadDeviceRegistration } from '../../engine/banepadDevice';
import { BanepadDeviceRegister } from './BanepadDeviceRegister';
import { BanepadDeviceShell } from './BanepadDeviceShell';
import { BanepadDeviceError } from './BanepadDeviceError';
import { BanepadDeviceOffline } from './BanepadDeviceOffline';
import { BanepadDeviceAppOnly } from './BanepadDeviceAppOnly';
import { canPairBanepadDevice } from '../../lib/nativeBridge';

// The path predicate MOVED to `app/routePaths.ts`, a module that imports
// nothing — see the note there. This one closed no cycle today, but it is the
// same shape that did: a router asking a COMPONENT which path it owns. Kept
// re-exported so existing importers and this route's tests are unaffected.
export { BANEPAD_DEVICE_ROUTE_PATHS, isBanepadDeviceRoutePath } from '../../app/routePaths';

/**
 * How often a paired pad re-validates its device token, so a revoke from the
 * Sentralen/server (or a token reaching end-of-life) propagates to the tablet
 * without a manual reload. TODO(prod): replace polling with a server heartbeat /
 * push, validating against the stored token hash.
 */
export const BANEPAD_DEVICE_REVALIDATE_MS = 30_000;

/**
 * `unreachable` is NOT `error`.
 *
 *   error        the server ANSWERED and refused — revoked, or an unknown
 *                token. The local token is worthless, and the pad must
 *                re-register.
 *   unreachable  we could not ask. The token may be perfectly good. Keeping it
 *                and trying again is the only correct response; clearing it
 *                would make a wifi blink cost a physical visit to re-pair a
 *                tablet mounted on a wall.
 */
type Phase = 'loading' | 'register' | 'paired' | 'error' | 'unreachable';

/** How soon a pad that could not reach the server tries again. */
export const BANEPAD_DEVICE_RETRY_MS = 5_000;

/**
 * Physical Banepad device route — the entry point a real tablet runs in kiosk
 * mode. It deliberately sits OUTSIDE the normal user auth gate (mounted from
 * main.tsx, not via Padel24App): a pad authenticates as a DEVICE, never with a
 * user/admin login.
 *
 * Flow:
 *   • no local token            → registration screen
 *   • token resolves to paired  → device shell (assigned court from the token)
 *   • token invalid/revoked     → error screen (clear local token → re-register)
 *
 * The assigned court is always derived from resolveBanepadDevice(token), so the
 * URL cannot grant or change court access.
 */
export function BanepadDeviceRoute() {
  const [phase, setPhase] = useState<Phase>('loading');
  const [registration, setRegistration] = useState<BanepadDeviceRegistration | null>(null);
  // May this client bind a NEW device to a court? Read once — the shell cannot
  // change under a running page. Paired pads are unaffected by this.
  const [mayPair] = useState(() => canPairBanepadDevice());

  // Resolve the stored device token (if any) to its court on mount.
  //
  // THE REJECTION PATH IS THE POINT. `resolveBanepadDevice` throws when the
  // server cannot be reached, and an unhandled rejection here leaves the pad in
  // `loading` FOR EVER — a blank «Laster…» on a wall-mounted tablet that nobody
  // is standing next to. So a failure becomes `unreachable`, the token is KEPT,
  // and the effect below retries on its own.
  const [resolveAttempt, setResolveAttempt] = useState(0);
  // When the current outage started, so the offline screen can say how long it
  // has been out. Set once per outage — a retry that also fails must not reset
  // the clock, or the screen would read "0 sekunder".
  const [unreachableSince, setUnreachableSince] = useState<number | null>(null);
  useEffect(() => {
    let active = true;
    (async () => {
      // ── «AM I PAIRED» IS A FLAG NOW, NOT A TOKEN (#663) ────────
      //
      // The credential is an httpOnly cookie this code cannot read, so the boot
      // gate asks the only question it still can — and asks it LOCALLY, which is
      // what keeps a rebooted pad off the pairing screen during an outage (#490).
      // A tablet paired before the cookie answers yes on its legacy token.
      if (!isBanepadDevicePaired()) {
        if (active) setPhase('register');
        return;
      }
      let resolved: BanepadDeviceRegistration | null;
      try {
        resolved = await resolveBanepadDevice();
      } catch {
        // Could not ASK. Not a refusal — see the Phase comment. The pairing stays
        // on the device.
        if (active) {
          setUnreachableSince((prev) => prev ?? Date.now());
          setPhase('unreachable');
        }
        return;
      }
      if (!active) return;
      if (resolved) {
        // ── THE HANDSHAKE'S LAST STEP (#663, #666 review) ────────
        //
        // The request SUCCEEDED, so `requireDeviceAuth` has just set `spd_session`
        // on that very response — which is the only reason it is safe to take the
        // raw token out of storage. Doing it any earlier would unpair a tablet
        // that had not yet been seeded.
        retireLegacyDeviceToken();
        setUnreachableSince(null);
        setRegistration(resolved);
        setPhase('paired');
      } else {
        // Paired locally but no longer an active device (revoked/invalid).
        setPhase('error');
      }
    })();
    return () => {
      active = false;
    };
  }, [resolveAttempt]);

  // A pad that could not reach the server retries by itself. Nobody is standing
  // next to a kiosk to press a button, so the manual retry on the screen is the
  // fallback, not the mechanism.
  useEffect(() => {
    if (phase !== 'unreachable') return undefined;
    const id = setTimeout(() => setResolveAttempt((n) => n + 1), BANEPAD_DEVICE_RETRY_MS);
    return () => clearTimeout(id);
  }, [phase, resolveAttempt]);

  // While paired, periodically re-validate the token so a revoke from the
  // Sentralen/server (or token end-of-life) moves the pad to the error state on
  // its own. The pad never un-pairs itself — this only reacts to the authority.
  useEffect(() => {
    if (phase !== 'paired') return undefined;
    let cancelled = false;
    const id = setInterval(async () => {
      let resolved: BanepadDeviceRegistration | null;
      try {
        resolved = isBanepadDevicePaired() ? await resolveBanepadDevice() : null;
      } catch {
        // COULD NOT ASK, while already paired. Milder than the mount case: the
        // pad has a registration and a court on screen, so it keeps both and
        // asks again next interval. Treating this as a revocation would blank a
        // working pad every time the wifi hiccuped; leaving it unhandled would
        // be an unhandled rejection every 30 seconds for as long as the outage
        // lasted.
        return;
      }
      if (cancelled) return;
      if (resolved) {
        setRegistration(resolved); // keep court name / details fresh
      } else {
        setRegistration(null);
        setPhase('error');
      }
    }, BANEPAD_DEVICE_REVALIDATE_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [phase]);

  // Successful registration: record that this tablet is paired and show the
  // assigned court.
  //
  // NO CREDENTIAL IS STORED OR RETAINED (#663, #668 review). Pairing set the
  // httpOnly cookie; the registration that arrives here carries
  // `deviceToken: null` by construction, so putting it in React state for the
  // session keeps no copy of the secret. An earlier version of this comment
  // claimed the token was «read no further than this callback» — and then this
  // callback held it in state for as long as the pad was mounted.
  const handlePaired = useCallback((reg: BanepadDeviceRegistration) => {
    markBanepadDevicePaired();
    setRegistration(reg);
    setPhase('paired');
  }, []);

  // Recovery from an already-dead token (revoked at the Sentralen/server or
  // EOL): clear ONLY this tablet's local token and return to registration. This
  // is NOT a self-unpair — it is reachable solely from the error screen, after
  // the authority has already invalidated the device.
  const handleReset = useCallback(() => {
    clearBanepadDevicePairing();
    // And what the old court was doing (#479) — a tablet being returned to
    // registration should not carry the previous court's players with it.
    clearPadLastKnown();
    setRegistration(null);
    setPhase('register');
  }, []);

  if (phase === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg-soft">
        <span className="text-ink-3 font-mono uppercase tracking-wider text-sm">Laster…</span>
      </div>
    );
  }
  if (phase === 'paired' && registration) {
    // NO StoreProvider (#185). The pad reads its own court from /pad/state
    // with its device token; it no longer mounts the club's entire dataset to
    // filter one court out of it in the browser.
    return <BanepadDeviceShell registration={registration} />;
  }
  if (phase === 'error') {
    return <BanepadDeviceError onReset={handleReset} />;
  }
  if (phase === 'unreachable') {
    // The token is still on this device, deliberately. `onRetry` only shortcuts
    // the timer above — it is not what makes recovery possible.
    return (
      <BanepadDeviceOffline
        courtName={registration?.courtName || 'Dette nettbrettet'}
        since={unreachableSince ?? Date.now()}
        onRetry={() => setResolveAttempt((n) => n + 1)}
      />
    );
  }
  // Pairing surface: the kiosk app (or localhost) only. A plain browser gets an
  // explanation instead of the code field.
  if (!mayPair) return <BanepadDeviceAppOnly />;
  return <BanepadDeviceRegister onPaired={handlePaired} />;
}
